import * as React from 'react'
import {
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

const TWILIO_ALERT_ALLIANCE_PLUGIN_KEY = 'twilio_alert_alliance'

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

function FieldLabel({ children }) {
    return (
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {children}
        </Typography>
    )
}

function FieldHint({ children }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                {children}
            </Typography>
        </Stack>
    )
}

export default function AllianceCallAlertPanel({
    userPlugins,
    onChange,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Enable Twilio Alliance Player Alert',
        'Enable phone-call alerts for alliance contacts configured on this bot',
        'Account SID',
        'Twilio Account SID used for outgoing calls',
        'Auth Token',
        'Twilio Auth Token used for secure API access',
        'Twilio Phone Number',
        'The Twilio phone number that places the call',
        'Contacts',
        'Contacts matched by player name. Format: name:phone;name2:phone2',
        'This section is linked directly to twilio_alert_alliance.js'
    ])

    const pluginConfig = userPlugins?.[TWILIO_ALERT_ALLIANCE_PLUGIN_KEY] ?? {}
    const enabled = Boolean(pluginConfig.state)
    const sid = String(pluginConfig.sid ?? '')
    const token = String(pluginConfig.token ?? '')
    const from = String(pluginConfig.from ?? '')
    const contacts = String(pluginConfig.contacts ?? '')

    const updateConfig = updater => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[TWILIO_ALERT_ALLIANCE_PLUGIN_KEY] ??= {}
        updater(nextPlugins[TWILIO_ALERT_ALLIANCE_PLUGIN_KEY])
        onChange?.(nextPlugins)
    }

    return (
        <Stack spacing={2.2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5}>
                <Stack spacing={0.5} sx={{ flex: 1, pt: 0.3 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <Switch
                            checked={enabled}
                            onChange={event => updateConfig(config => {
                                config.state = event.target.checked
                            })}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {t('Enable Twilio Alliance Player Alert')}
                        </Typography>
                    </Stack>
                    <FieldHint>{t('Enable phone-call alerts for alliance contacts configured on this bot')}</FieldHint>
                </Stack>

                <Stack spacing={0.4} sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneInTalkRoundedIcon sx={{ color: 'primary.light', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('This section is linked directly to twilio_alert_alliance.js')}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel>{t('Account SID')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={sid}
                        onChange={event => updateConfig(config => {
                            config.sid = event.target.value
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    />
                    <FieldHint>{t('Twilio Account SID used for outgoing calls')}</FieldHint>
                </Stack>

                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel>{t('Auth Token')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        type="password"
                        value={token}
                        onChange={event => updateConfig(config => {
                            config.token = event.target.value
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    />
                    <FieldHint>{t('Twilio Auth Token used for secure API access')}</FieldHint>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel>{t('Twilio Phone Number')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={from}
                        onChange={event => updateConfig(config => {
                            config.from = event.target.value
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+</InputAdornment>
                        }}
                    />
                    <FieldHint>{t('The Twilio phone number that places the call')}</FieldHint>
                </Stack>

                <Stack spacing={0.75} sx={{ flex: 1 }}>
                    <FieldLabel>{t('Contacts')}</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={contacts}
                        onChange={event => updateConfig(config => {
                            config.contacts = event.target.value
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    />
                    <FieldHint>{t('Contacts matched by player name. Format: name:phone;name2:phone2')}</FieldHint>
                </Stack>
            </Stack>
        </Stack>
    )
}
